import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { HomeTaskDetailsCard } from '@/components/HomeTaskModal/HomeTaskDetailsCard';
import { MessageThreadSection } from '@/components/HomeTaskModal/MessageThreadSection';
import { useHomeTask } from '@/components/HomeTaskModal/hooks/useHomeTask';
import { ModalHeader } from '@/components/Modal/ModalHeader';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useUserContext } from '@/auth/UserContext';
import type { Attachment } from '@/types/attachment';
import { PersonType } from '@/types/user';

interface Props {
  homeTaskId: number;
  onClose: () => void;
  onBack?: () => void;
}

export const HomeTaskView: React.FC<Props> = ({
  homeTaskId,
  onClose,
  onBack,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );

  const title = t('diary.homeTask.submitAnswers');
  const isStudent = user?.personType === PersonType.Student;
  const { data, isLoading } = useHomeTask(homeTaskId);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ModalHeader title={title} onClose={onClose} onBack={onBack} />

      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <PageSpinner className="size-[22px] text-subtle-foreground" />
        </div>
      )}

      {!isLoading && !data && (
        <EmptyState label={t('diary.homeTask.notFound')} />
      )}

      {!isLoading && !!data && (
        <AutoHideScrollArea
          hasTopShadow
          className="flex min-h-0 flex-1 flex-col"
          viewportClassName="[&>div]:h-full"
        >
          <div className="flex min-h-full flex-col gap-4">
            <div className="px-4 md:px-5 md:pt-2">
              <HomeTaskDetailsCard
                homeTask={data.homeTask}
                onPreview={setPreviewAttachment}
              />
            </div>

            <MessageThreadSection
              isStudent={isStudent}
              homeTaskId={homeTaskId}
              thread={data.messageThread}
              onPreview={setPreviewAttachment}
            />
          </div>
        </AutoHideScrollArea>
      )}

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onDownloaded={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
};
